import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { usePageTitle } from "../components/PageTitle";
import { applyDirection } from "../lib/direction";

function extractJoinCode(input: string): string | null {
  if (!input) return null;
  const s = input.trim();

  // Full URL? Try to pull /join/<code> or ?invite=...
  try {
    const u = new URL(s);
    const m = u.pathname.match(/\/join\/([^/?#]+)/i);
    if (m?.[1]) return m[1];
    const qp =
      u.searchParams.get("invite") ||
      u.searchParams.get("team") ||
      u.searchParams.get("code");
    if (qp) return qp;
  } catch {
    // not a URL
  }

  // Path-like /join/<code>
  const p = s.match(/\/join\/([^/?#]+)/i);
  if (p?.[1]) return p[1];

  // Raw code (alnum, hyphen/underscore)
  if (/^[A-Za-z0-9_-]{4,64}$/.test(s)) return s;

  return null;
}

function normalizeToJoinHref(value: string): string | null {
  const code = extractJoinCode(value);
  return code ? `/join/${encodeURIComponent(code)}` : null;
}

export default function Landing() {
  const { t, i18n } = useTranslation();
  usePageTitle(t("app.joinGame"));
  const navigate = useNavigate();

  const [invite, setInvite] = useState("");
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    applyDirection(i18n.language);
  }, [i18n.language]);

  // Auto-redirect if URL contains ?invite=... / ?team=... / ?code=...
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const possible = sp.get("invite") || sp.get("team") || sp.get("code");
    const href = possible ? normalizeToJoinHref(possible) : null;
    if (href) {
      // keep SPA navigation, but replace history entry like window.location.replace
      navigate(href, { replace: true });
    }
  }, [navigate]);

  const joinHref = useMemo(() => normalizeToJoinHref(invite), [invite]);

  function handleJoin() {
    if (!joinHref) {
      setInvalid(true);
      return;
    }
    navigate(joinHref);
  }

  return (
    <AppShell width="center">
      <section className="auth-card rise">
        {/* No eyebrow here: the header wordmark already says the same thing. */}
        <h1 className="auth-card__title">{t("joinGame.joinurTeam")}</h1>
        <p className="auth-card__text">{t("joinGame.joinTip")}</p>

        <div className="field">
          <label className="field__label" htmlFor="invite">
            {t("app.joinGame")}
          </label>
          {/* Input and button live in one group; the group mirrors itself in
              Arabic, so there is no need to swap their order by hand. */}
          <div className="p-inputgroup">
            <InputText
              id="invite"
              value={invite}
              autoComplete="off"
              aria-invalid={invalid}
              aria-describedby={invalid ? "invite-error" : undefined}
              onChange={(e) => {
                setInvite(e.target.value);
                setInvalid(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleJoin();
              }}
              placeholder={t("joinGame.joinholder")}
              /* Invite codes and links are always Latin. */
              className="force-ltr"
            />
            <Button
              type="button"
              onClick={handleJoin}
              disabled={!invite.trim()}
              label={t("landing.joinButton", {
                defaultValue: t("joinGame.joinBtn"),
              })}
            />
          </div>

          {invalid && (
            <p className="field__error" id="invite-error" role="alert">
              <i className="pi pi-exclamation-circle" aria-hidden="true" />
              {t("joinGame.joinError")}
            </p>
          )}
        </div>
      </section>
    </AppShell>
  );
}
